/**
 * Phase 67-05 Wave 4 — fleet-state WebSocket hook.
 *
 * Installs a long-lived WS connection at AppShell mount, routes server
 * messages into the TanStack Query cache:
 *
 *   snapshot       → queryClient.setQueryData(qk.machines("all"), machines)
 *   machine_update → merge fields into the existing per-cpu_id entry
 *   stale_event    → invalidate qk.machines (stale subset is derived)
 *   audit          → invalidate qk.audit branch
 *
 * TanStack remains the single source of truth — existing components
 * (`useMachines()`, `useStaleKiosks()`, `useRebootLoops()`,
 * `RecoveryBanner`, etc.) keep their API.  When fallbackMode flips to
 * true, the hook exposes a flag the polling-aware queries can read to
 * keep their 30s refetch alive.
 *
 * Auth: reads `protos_token` from localStorage (Phase 66 contract).
 * If no token is present, the hook is a no-op (no connection attempt).
 */

import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { WsClient, type FleetMessage } from "@/lib/wsClient";
import { qk } from "@/lib/queryKeys";
import { setWsState } from "@/stores/wsConnectionStore";

/**
 * Phase 72 — minimal audit-event PubSub. Phase 72 DigitalTwinRestoreProgress
 * (per UI-SPEC §Restore-in-flight progress UX) needs raw audit event
 * notifications filtered by cpu_id + a 6-op plain-snake_case allowlist
 * (D-DT-19 amendment per Codex MEDIUM #1 fix). useFleetWebSocket is the
 * single owner of the WsClient; adding a side-channel emitter here keeps
 * the contract centralized without leaking the underlying WsClient.
 *
 * Subscribers receive every audit event the broadcaster pushes; cpu_id +
 * op-allowlist filtering happens in the consuming component (so the
 * filter table can evolve independently of this hook).
 */
export type FleetAuditEvent = {
  /** Operation name (e.g. "digital_twin_restore_started"). */
  event: string;
  /** Per-machine routing key. */
  cpu_id: string;
  /** Server ISO timestamp. */
  ts: string;
};

const _auditSubscribers = new Set<(evt: FleetAuditEvent) => void>();

export function subscribeFleetAudit(
  fn: (evt: FleetAuditEvent) => void,
): () => void {
  _auditSubscribers.add(fn);
  return () => {
    _auditSubscribers.delete(fn);
  };
}

function _dispatchFleetAudit(evt: FleetAuditEvent): void {
  for (const fn of _auditSubscribers) {
    try {
      fn(evt);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[useFleetWebSocket] audit subscriber threw", e);
    }
  }
}

/**
 * Phase 73 Plan 05 — DEV-only test hook for Playwright e2e specs.
 *
 * Exposed on `window.__dispatchFleetAudit` in dev/test bundles so the
 * `force-recovery-window.spec.js` D-AF-05 badge test can fire a
 * synthetic `auth_fail_ratelimit_hit` audit event without needing a
 * live MQTT auth flow. Production builds strip this exposure via the
 * `import.meta.env.DEV` guard (handled at the call site below).
 */
if (typeof window !== "undefined" && import.meta.env.DEV) {
  (
    window as unknown as {
      __dispatchFleetAudit?: (evt: FleetAuditEvent) => void;
    }
  ).__dispatchFleetAudit = _dispatchFleetAudit;
}

const DEFAULT_WS_URL =
  (import.meta as ImportMeta & { env: { VITE_WS_URL?: string } }).env?.VITE_WS_URL ??
  "/ws/v1/fleet";

export type FleetWebSocketState = {
  connected: boolean;
  fallbackMode: boolean;
  lastEventTs: number | null;
};

export function useFleetWebSocket(): FleetWebSocketState {
  const queryClient = useQueryClient();
  const clientRef = useRef<WsClient | null>(null);
  const [state, setLocalState] = useState<FleetWebSocketState>({
    connected: false,
    fallbackMode: false,
    lastEventTs: null,
  });
  // Phase 67-05 Wave 6 — mirror state to the global wsConnectionStore so
  // polling-aware query hooks can pick the right refetchInterval.
  const setState = (
    updater: (prev: FleetWebSocketState) => FleetWebSocketState,
  ) => {
    setLocalState((prev) => {
      const next = updater(prev);
      setWsState(next);
      return next;
    });
  };

  useEffect(() => {
    const token = localStorage.getItem("protos_token");
    if (!token) {
      // Anonymous — hook is dormant until login flips the token in.
      return undefined;
    }

    // Resolve absolute WS URL — relative paths need scheme conversion.
    const wsUrl = (() => {
      if (/^wss?:\/\//.test(DEFAULT_WS_URL)) return DEFAULT_WS_URL;
      const loc = typeof window !== "undefined" ? window.location : null;
      if (!loc) return DEFAULT_WS_URL;
      const proto = loc.protocol === "https:" ? "wss:" : "ws:";
      return `${proto}//${loc.host}${DEFAULT_WS_URL}`;
    })();

    const client = new WsClient({
      url: wsUrl,
      token,
      onMessage: (msg: FleetMessage) => {
        setState((s) => ({ ...s, lastEventTs: Date.now() }));
        switch (msg.type) {
          case "snapshot": {
            queryClient.setQueryData(qk.machines("all"), msg.machines);
            break;
          }
          case "machine_update": {
            const prev =
              (queryClient.getQueryData(qk.machines("all")) as
                | Array<Record<string, unknown> & { cpu_id?: string }>
                | undefined) ?? [];
            const next = prev.map((m) =>
              m.cpu_id === msg.cpu_id ? { ...m, ...msg.fields } : m,
            );
            queryClient.setQueryData(qk.machines("all"), next);
            // W-3 (Phase 79 Plan 03 LIVE-UPDATE FIX): the list-only merge
            // above touches qk.machines("all") but useMachineDetail reads
            // from `[...qk.machines(tenant), cpuId]` — a SEPARATE cache the
            // merge never reaches, so the detail TimezoneCard (and every
            // other detail-bound field) would NOT live-update. Propagate the
            // SAME fields to the per-cpu DETAIL caches.
            //
            // EXACT detail-key shape: key[0]==="machines" && key.length===3
            // && key[2]===cpu_id. List keys are length-2 (["machines",
            // tenant], e.g. qk.machines("all") / qk.machines("c-xxx")), so
            // the length===3 guard makes a list-key collision impossible.
            queryClient.setQueriesData(
              {
                predicate: (q) =>
                  q.queryKey[0] === "machines" &&
                  q.queryKey.length === 3 &&
                  q.queryKey[2] === msg.cpu_id,
              },
              (old) =>
                old ? { ...(old as object), ...msg.fields } : old,
            );
            break;
          }
          case "stale_event": {
            // Stale-derived UI is computed from useMachines(); invalidate
            // that branch so derived hooks re-evaluate.
            void queryClient.invalidateQueries({ queryKey: ["machines"] });
            break;
          }
          case "audit": {
            void queryClient.invalidateQueries({ queryKey: ["audit"] });
            // Phase 72 — fan-out raw audit events to the PubSub registry
            // for DigitalTwinRestoreProgress (cpu_id + plain-snake_case
            // op allowlist filter lives in the consumer).
            _dispatchFleetAudit({
              event: msg.event,
              cpu_id: msg.cpu_id,
              ts: msg.ts,
            });
            break;
          }
          default: {
            // Unknown message type — ignore.
            break;
          }
        }
      },
      onOpen: () => setState((s) => ({ ...s, connected: true })),
      onClose: () => setState((s) => ({ ...s, connected: false })),
      onFallback: (fallbackMode) => setState((s) => ({ ...s, fallbackMode })),
    });
    clientRef.current = client;

    // Dev hook for Playwright spec to force close + verify fallback.
    if (typeof window !== "undefined" && import.meta.env.DEV) {
      (window as unknown as { __fleetWS?: WsClient }).__fleetWS = client;
    }

    return () => {
      client.close();
      clientRef.current = null;
    };
  }, [queryClient]);

  return state;
}
